import React, { useState } from 'react';
import { Calculator, AlertTriangle, Lightbulb, TrendingDown, CreditCard, FileText, Users, Shield } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

interface DebtCalculation {
  debtAmount: number;
  interestRate: number;
  monthlyInterest: number;
  yearlyInterest: number;
  totalInOneYear: number;
}

const DebtManagement: React.FC = () => {
  useSEO({
    title: 'Como Sair das Dívidas e Renegociar com Desconto em 2026 | O Teu Dinheiro',
    description: 'Estratégias práticas para quitar dívidas de cartão de crédito e empréstimos, usar a portabilidade de crédito do Open Finance e limpar seu nome.',
    keywords: 'como sair das dívidas, renegociação de dívidas 2026, juros cartão de crédito, limpar nome serasa, portabilidade de crédito',
    url: 'https://www.oteudinheiro.com.br/dividas'
  });
  const [debtAmount, setDebtAmount] = useState<string>('');
  const [interestRate, setInterestRate] = useState<string>('');
  const [calculation, setCalculation] = useState<DebtCalculation | null>(null);
  const [error, setError] = useState<string>('');

  const calculateDebt = () => {
    const amount = parseFloat(debtAmount);
    const rate = parseFloat(interestRate);

    if (isNaN(amount) || isNaN(rate)) {
      setError('Por favor, preencha todos os campos com valores válidos.');
      setCalculation(null);
      return;
    }

    if (amount <= 0 || rate < 0) {
      setError('Os valores devem ser maiores que zero.');
      setCalculation(null);
      return;
    }

    const monthlyInterest = rate / 100;
    const monthlyPayment = amount * monthlyInterest;
    const yearlyInterest = monthlyInterest * 12;
    const yearlyPayment = amount * yearlyInterest;

    setCalculation({
      debtAmount: amount,
      interestRate: rate,
      monthlyInterest: monthlyPayment,
      yearlyInterest: yearlyPayment,
      totalInOneYear: amount + yearlyPayment
    });

    setError('');
  };

  const strategies = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Liste todas as dívidas',
      description: 'Cartão de crédito, empréstimos, contas em atraso'
    },
    {
      icon: <TrendingDown className="w-6 h-6" />,
      title: 'Priorize por juros',
      description: 'Pague primeiro as dívidas com juros mais altos'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Negocie com credores',
      description: 'Muitos aceitam parcelamento ou desconto'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Evite novos empréstimos',
      description: 'Foque em quitar o que já tem'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-orange-50 section-padding">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Gerenciando suas Dívidas
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Aprenda estratégias práticas para sair do vermelho e organizar suas finanças. 
            O primeiro passo é entender o impacto dos juros e criar um plano de ação.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Como Organizar suas Dívidas
                </h2>
                <div className="space-y-6">
                  {strategies.map((strategy, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <div className="text-red-600">
                          {strategy.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {strategy.title}
                        </h3>
                        <p className="text-gray-600">
                          {strategy.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tip-box">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="w-6 h-6 text-secondary-600 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      💡 Dica Importante
                    </h4>
                    <p className="text-gray-700">
                      Comece sempre pela dívida com juros mais altos. Cartão de crédito 
                      pode ter juros de até 400% ao ano! Quanto antes você pagar, 
                      menos dinheiro jogará fora com juros.
                    </p>
                  </div>
                </div>
              </div>

              <div className="warning-box">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-accent-600 mt-1" />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      ⚠️ Cuidado com Armadilhas
                    </h4>
                    <p className="text-gray-700">
                      Evite fazer novos empréstimos para pagar dívidas antigas. 
                      Isso pode criar um ciclo vicioso de endividamento. 
                      Foque em aumentar sua renda e reduzir gastos.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="card p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Calculadora de Dívidas
                </h3>
                <p className="text-gray-600">
                  Calcule o impacto dos juros em suas dívidas
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor da Dívida (R$)
                  </label>
                  <input
                    type="number"
                    value={debtAmount}
                    onChange={(e) => setDebtAmount(e.target.value)}
                    placeholder="0,00"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Juros Mensal (%)
                  </label>
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    placeholder="0"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                <button
                  onClick={calculateDebt}
                  className="w-full btn btn-primary bg-red-600 hover:bg-red-700 focus:ring-red-500"
                >
                  Calcular
                </button>

                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {calculation && (
                  <div className="p-6 bg-gray-50 rounded-lg space-y-3">
                    <h4 className="font-semibold text-gray-900 text-center mb-4">
                      Resultado do Cálculo
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-gray-600">Dívida:</div>
                      <div className="font-medium">R$ {calculation.debtAmount.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Juros Mensais:</div>
                      <div className="font-medium text-red-600">R$ {calculation.monthlyInterest.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Juros Anuais:</div>
                      <div className="font-medium text-red-600">R$ {calculation.yearlyInterest.toFixed(2)}</div>
                      
                      <div className="text-gray-600">Total em 1 ano:</div>
                      <div className="font-bold text-red-700">R$ {calculation.totalInOneYear.toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Tips Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Estratégias Adicionais
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Além de organizar suas dívidas, considere estas estratégias para 
              melhorar sua situação financeira a longo prazo.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Cartão de Crédito
              </h3>
              <p className="text-gray-600">
                Pague sempre o valor total da fatura. Se não conseguir, 
                pague pelo menos o valor mínimo e negocie os juros.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Reduza Gastos
              </h3>
              <p className="text-gray-600">
                Identifique gastos desnecessários e corte-os. 
                Use o dinheiro economizado para pagar suas dívidas.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Fundo de Emergência
              </h3>
              <p className="text-gray-600">
                Após quitar as dívidas, crie um fundo de emergência 
                para evitar novos endividamentos.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DebtManagement;
